import { Gauge, WifiOff } from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { AdminCouncilInfo } from "@/gen/api"

interface FleetHealthByCouncilWidgetProps {
  councilInfos: AdminCouncilInfo[]
}

function safeNumber(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value))
}

type CouncilFleetHealthRow = {
  id: string
  name: string
  shortName: string
  totalCount: number
  onlineCount: number
  offlineCount: number
  healthPercentage: number
  onlinePercentage: number
  offlinePercentage: number
}

function FleetHealthTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null

  const item = payload[0]?.payload as CouncilFleetHealthRow | undefined
  if (!item) return null

  return (
    <div className="rounded-lg border bg-card p-3 shadow-lg">
      <p className="font-medium">{item.name}</p>
      <div className="space-y-1 text-sm">
        <p className="text-muted-foreground">Total: {item.totalCount.toLocaleString()}</p>
        <p className="text-chart-2">Online: {item.onlineCount.toLocaleString()}</p>
        <p className="text-destructive">Offline: {item.offlineCount.toLocaleString()}</p>
        <p className="font-medium">Health: {item.healthPercentage.toFixed(1)}%</p>
      </div>
    </div>
  )
}

export function FleetHealthByCouncilWidget({ councilInfos }: FleetHealthByCouncilWidgetProps) {
  const rows = councilInfos
    .map((council) => {
      const totalCount = safeNumber(council.deviceCount)
      const onlineCount = safeNumber(council.activeCount)
      const offlineCount =
        typeof council.offlineCount === "number"
          ? safeNumber(council.offlineCount)
          : Math.max(totalCount - onlineCount, 0)
      const healthPercentage = totalCount > 0 ? (onlineCount * 100) / totalCount : 0
      const offlinePercentage = totalCount > 0 ? (offlineCount * 100) / totalCount : 0

      return {
        id: council.id || council.name || "unknown",
        name: council.name || "Unknown",
        shortName:
          (council.name || "Unknown").length > 16
            ? `${(council.name || "Unknown").slice(0, 14)}...`
            : council.name || "Unknown",
        totalCount,
        onlineCount,
        offlineCount,
        healthPercentage: clampPercent(healthPercentage),
        onlinePercentage: clampPercent(healthPercentage),
        offlinePercentage: clampPercent(offlinePercentage),
      }
    })
    .filter((item) => item.totalCount > 0)
    .sort((a, b) => a.healthPercentage - b.healthPercentage || b.totalCount - a.totalCount)

  const totalDevices = rows.reduce((sum, item) => sum + item.totalCount, 0)
  const totalOnline = rows.reduce((sum, item) => sum + item.onlineCount, 0)
  const totalOffline = rows.reduce((sum, item) => sum + item.offlineCount, 0)
  const chartWidth = rows.length > 6 ? rows.length * 96 : "100%"

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge className="size-5" />
            Fleet Health by Customer
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Online and offline devices across all customers
        </p>
      </CardHeader>
      <CardContent>
        {rows.length > 0 ? (
          <div className="space-y-4">
            <div className="overflow-x-auto pb-2">
              <div style={{ minWidth: chartWidth, height: 430 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={rows}
                    barCategoryGap="42%"
                    barSize={44}
                    margin={{ top: 16, right: 24, left: 8, bottom: 58 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" vertical={false} />
                    <XAxis
                      dataKey="shortName"
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={82}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[0, 100]}
                      allowDecimals={false}
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip content={<FleetHealthTooltip />} />
                    <Legend
                      formatter={(value) => (
                        <span className="text-sm text-muted-foreground">{value}</span>
                      )}
                    />
                    <Bar
                      dataKey="onlinePercentage"
                      stackId="fleet-health"
                      fill="hsl(var(--chart-2))"
                      name="Online"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="offlinePercentage"
                      stackId="fleet-health"
                      fill="hsl(var(--destructive))"
                      name="Offline"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 border-t pt-4 text-center text-sm">
              <div>
                <p className="text-muted-foreground">Total</p>
                <p className="font-semibold">{totalDevices.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Online</p>
                <p className="font-semibold text-chart-2">{totalOnline.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Offline</p>
                <p className="font-semibold text-destructive">{totalOffline.toLocaleString()}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <WifiOff className="mb-3 size-12 opacity-30" />
            <p className="text-sm">No customer fleet data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
